import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiClient } from '@/services/api'
import {
  ArrowLeft, Users, Target, BarChart2, Trophy, BookOpen, Award,
  ChevronUp, ChevronDown, Download, FileSpreadsheet, FileText,
  Loader2, ExternalLink,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { useTerminology } from '@/hooks/useTerminology'
import type { CourseRunOverview, CourseStudent } from '@/types'
import {
  generateRunExcelReport,
  generateRunPdfReport,
  getCurrentPdfTheme,
  type LessonReportData,
  type RunReportData,
  type RawResponse,
} from '@/utils/reportGenerator'

type SortField = 'totalScore' | 'avgScore' | 'lessonsAttended' | 'responseRate' | 'totalResponses'
type TabKey = 'summary' | 'lessons' | 'students' | 'export'

export function CourseRunReport() {
  const { organizationId, courseId, runId } = useParams<{ organizationId: string; courseId: string; runId: string }>()
  const navigate = useNavigate()
  const t = useTerminology()

  const [data, setData] = useState<CourseRunOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('summary')
  const [sortField, setSortField] = useState<SortField>('totalScore')
  const [sortAsc, setSortAsc] = useState(false)

  useEffect(() => {
    if (!courseId || !runId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await apiClient.getCourseRunOverview(courseId, runId)
        setData((res.data as any)?.run ? res.data as any : res.data)
      } catch (err: any) {
        const apiError = err?.response?.data?.error
        setError(typeof apiError === 'string' ? apiError : apiError?.message || err?.message || 'Failed to load run report')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [courseId, runId])

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-theme-text-muted font-mono">LOADING RUN REPORT</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error ?? 'Report not available'}</p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-surface-hover rounded-lg text-theme-text hover:bg-surface-hover transition-colors">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const { run, summary, lessonBreakdown, studentLeaderboard } = data

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Header */}
      <div className="border-b border-theme-border bg-chrome-bg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-surface-hover text-theme-text hover:bg-surface-hover transition-colors shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-green-500">{run.title || 'Run'} — Report</h1>
              <div className="flex items-center gap-2 mt-0.5 text-sm text-theme-text-muted font-mono flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${
                  run.status === 'completed' ? 'bg-green-500/10 text-green-500'
                  : run.status === 'started' ? 'bg-yellow-500/10 text-yellow-500'
                  : 'bg-yellow-500/10 text-yellow-500'
                }`}>{run.status}</span>
                <span>&middot; {lessonBreakdown.length} {t.lesson.lowerPlural} &middot; {summary.totalStudents} students</span>
                {run.startedAt && <span>&middot; {new Date(run.startedAt).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-tour="run-report-summary">
          <StatCard icon={Users} label="Total Students" value={summary.totalStudents} color="green" />
          <StatCard icon={BookOpen} label="Total Activities" value={summary.totalActivities} color="blue" />
          <StatCard icon={Target} label="Response Rate" value={`${summary.responseRate}%`} color="yellow" />
          <StatCard icon={Trophy} label="Avg Score" value={summary.avgScore} color="purple" />
          <StatCard icon={BarChart2} label="Completion" value={`${summary.completionRate}%`} color="cyan" />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-theme-border">
          {([
            { key: 'summary', label: 'Summary' },
            { key: 'lessons', label: t.lesson.titlePlural },
            { key: 'students', label: 'Students' },
            { key: 'export', label: 'Export' },
          ] as { key: TabKey; label: string }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 font-medium border-b-2 transition-colors text-sm ${
                activeTab === tab.key
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-theme-text-muted hover:text-theme-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <SummaryTab lessonBreakdown={lessonBreakdown} summary={summary} />
        )}
        {activeTab === 'lessons' && (
          <LessonsTab lessonBreakdown={lessonBreakdown} navigate={navigate} />
        )}
        {activeTab === 'students' && (
          <StudentsTab
            students={studentLeaderboard}
            sortField={sortField}
            sortAsc={sortAsc}
            onSort={(field) => {
              if (field === sortField) setSortAsc(!sortAsc)
              else { setSortField(field); setSortAsc(false) }
            }}
          />
        )}
        {activeTab === 'export' && (
          <ExportTab courseId={courseId!} runId={runId!} runTitle={run.title || 'Run'} lessonBreakdown={lessonBreakdown} />
        )}
      </div>
    </div>
  )
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) {
  const colorMap: Record<string, string> = {
    green: 'bg-green-900/30 text-green-400',
    blue: 'bg-blue-900/30 text-blue-400',
    yellow: 'bg-yellow-900/30 text-yellow-400',
    purple: 'bg-purple-900/30 text-purple-400',
    cyan: 'bg-cyan-900/30 text-cyan-400',
  }
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-surface-deep rounded-xl p-4 border border-theme-border">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${colorMap[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-theme-text-muted mt-0.5">{label}</div>
    </motion.div>
  )
}

function SummaryTab({ lessonBreakdown, summary }: { lessonBreakdown: CourseRunOverview['lessonBreakdown']; summary: CourseRunOverview['summary'] }) {
  const t = useTerminology()
  const chartData = lessonBreakdown.map(l => ({
    name: l.lessonName.length > 20 ? l.lessonName.slice(0, 18) + '...' : l.lessonName,
    responseRate: l.responseRate,
    avgScore: l.avgScore,
    students: l.studentCount,
  }))

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Key Metrics */}
      <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
        <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide mb-4">Key Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-theme-text-muted">Total Responses:</span> <span className="font-bold text-theme-text ml-1">{summary.totalResponses}</span></div>
          <div><span className="text-theme-text-muted">{`Completed ${t.lesson.titlePlural}:`}</span> <span className="font-bold text-theme-text ml-1">{summary.completedLessons} / {lessonBreakdown.length}</span></div>
          <div><span className="text-theme-text-muted">Avg Score:</span> <span className="font-bold text-theme-text ml-1">{summary.avgScore}</span></div>
          <div><span className="text-theme-text-muted">Response Rate:</span> <span className="font-bold text-theme-text ml-1">{summary.responseRate}%</span></div>
        </div>
      </div>

      {/* Per-Lesson Chart */}
      {chartData.length > 0 && (
        <div className="bg-surface-deep rounded-xl p-6 border border-theme-border">
          <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide mb-4">{`Per-${t.lesson.title} Performance`}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'var(--color-surface-deep)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }} />
              <Legend />
              <Bar dataKey="responseRate" name="Response Rate %" fill="#4ADE80" radius={[4, 4, 0, 0]} />
              <Bar dataKey="avgScore" name="Avg Score" fill="#60A5FA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  )
}

function LessonsTab({ lessonBreakdown, navigate }: { lessonBreakdown: CourseRunOverview['lessonBreakdown']; navigate: any }) {
  const t = useTerminology()
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
      {lessonBreakdown.length === 0 ? (
        <div className="text-center py-16 text-theme-muted">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{`No ${t.lesson.lowerPlural} in this run`}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-theme-text-muted border-b border-theme-border">
                <th className="pb-2 pr-4">#</th>
                <th className="pb-2 pr-4">{t.lesson.title}</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4 text-right">Students</th>
                <th className="pb-2 pr-4 text-right">Activities</th>
                <th className="pb-2 pr-4 text-right">Response Rate</th>
                <th className="pb-2 pr-4 text-right">Avg Score</th>
                <th className="pb-2 pr-4 text-right">Duration</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {lessonBreakdown.map((l, idx) => (
                <tr key={l.lessonId} className="border-b border-theme-border hover:bg-chrome-bg transition-colors">
                  <td className="py-3 pr-4 text-theme-muted font-mono">{idx + 1}</td>
                  <td className="py-3 pr-4 font-medium">{l.lessonName}</td>
                  <td className="py-3 pr-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      l.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                      l.status === 'live' ? 'bg-blue-500/10 text-blue-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>{l.status}</span>
                  </td>
                  <td className="py-3 pr-4 text-right">{l.studentCount}</td>
                  <td className="py-3 pr-4 text-right">{l.activityCount}</td>
                  <td className="py-3 pr-4 text-right">{l.responseRate}%</td>
                  <td className="py-3 pr-4 text-right">{l.avgScore}</td>
                  <td className="py-3 pr-4 text-right text-theme-text-muted">{l.duration}m</td>
                  <td className="py-3">
                    {(l.status === 'completed' || l.status === 'live') && (
                      <button
                        onClick={() => navigate(`/lessons/${l.lessonId}/report`)}
                        className="p-1.5 bg-surface-hover text-theme-text rounded-lg hover:bg-surface-hover transition-colors"
                        title={`View ${t.lesson.lower} report`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  )
}

function StudentsTab({
  students,
  sortField,
  sortAsc,
  onSort,
}: {
  students: CourseStudent[]
  sortField: SortField
  sortAsc: boolean
  onSort: (field: SortField) => void
}) {
  const t = useTerminology()
  const sorted = useMemo(() => {
    const arr = [...students]
    arr.sort((a, b) => {
      const av = a[sortField] ?? 0
      const bv = b[sortField] ?? 0
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
    return arr
  }, [students, sortField, sortAsc])

  const SortHeader = ({ field, label, className }: { field: SortField; label: string; className?: string }) => (
    <th
      className={`pb-2 pr-4 cursor-pointer select-none hover:text-theme-text transition-colors ${className ?? ''}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  )

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      {sorted.length === 0 ? (
        <div className="text-center py-16 text-theme-muted">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No student data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-theme-text-muted border-b border-theme-border">
                <th className="pb-2 pr-4">#</th>
                <th className="pb-2 pr-4">Student</th>
                <SortHeader field="lessonsAttended" label={t.lesson.titlePlural} className="text-right" />
                <SortHeader field="totalScore" label="Total Score" className="text-right" />
                <SortHeader field="avgScore" label="Avg Score" className="text-right" />
                <SortHeader field="totalResponses" label="Responses" className="text-right" />
                <SortHeader field="responseRate" label="Response Rate" className="text-right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((s, idx) => (
                <tr key={idx} className="border-b border-theme-border hover:bg-chrome-bg transition-colors">
                  <td className="py-3 pr-4 text-theme-muted font-mono">{idx + 1}</td>
                  <td className="py-3 pr-4 font-medium flex items-center gap-2">
                    {idx < 3 && <Award className={`w-4 h-4 ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-theme-text' : 'text-orange-400'}`} />}
                    {s.displayName}
                  </td>
                  <td className="py-3 pr-4 text-right">{s.lessonsAttended}</td>
                  <td className="py-3 pr-4 text-right font-bold">{s.totalScore}</td>
                  <td className="py-3 pr-4 text-right">{s.avgScore}</td>
                  <td className="py-3 pr-4 text-right">{s.totalResponses}</td>
                  <td className="py-3 pr-4 text-right">{s.responseRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  )
}

function ExportTab({ courseId, runId, runTitle, lessonBreakdown }: { courseId: string; runId: string; runTitle: string; lessonBreakdown: CourseRunOverview['lessonBreakdown'] }) {
  const t = useTerminology()
  const [excelLoading, setExcelLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [progress, setProgress] = useState('')

  const fetchReportData = async (): Promise<RunReportData> => {
    const lessonData: LessonReportData[] = []

    for (let i = 0; i < lessonBreakdown.length; i++) {
      const lb = lessonBreakdown[i]
      setProgress(`Loading lesson ${i + 1}/${lessonBreakdown.length}...`)

      const [lessonRes, analyticsRes] = await Promise.allSettled([
        apiClient.getLesson(lb.lessonId),
        apiClient.getLessonPlayersAnalytics(lb.lessonId),
      ])

      const fullLesson = lessonRes.status === 'fulfilled'
        ? ((lessonRes.value.data as any)?.lesson ?? lessonRes.value.data)
        : { id: lb.lessonId, name: lb.lessonName, activities: [], players: [] }
      const activities = fullLesson.activities ?? []
      const players = fullLesson.players ?? []

      const rawAnalytics: any[] = analyticsRes.status === 'fulfilled'
        ? ((analyticsRes.value.data as any)?.players ?? analyticsRes.value.data ?? [])
        : []
      const playerAnalytics: Record<string, any> = {}
      for (const a of rawAnalytics) playerAnalytics[a.id] = a

      const responseResults = await Promise.allSettled(
        activities.map((act: any) => apiClient.getResponses(fullLesson.id, act.id))
      )
      const activityResponses: Record<string, RawResponse[]> = {}
      activities.forEach((act: any, idx: number) => {
        const result = responseResults[idx]
        activityResponses[act.id] = result.status === 'fulfilled'
          ? ((result.value.data as any)?.responses ?? result.value.data ?? [])
          : []
      })

      lessonData.push({ lesson: fullLesson, activities, activityResponses, players, playerAnalytics })
    }

    setProgress('')
    return { runTitle, lessons: lessonData }
  }

  const handleExcel = async () => {
    setExcelLoading(true)
    try {
      const data = await fetchReportData()
      await generateRunExcelReport(data)
    } catch (e) { }
    finally { setExcelLoading(false); setProgress('') }
  }

  const handlePdf = async () => {
    setPdfLoading(true)
    try {
      const data = await fetchReportData()
      await generateRunPdfReport(data, getCurrentPdfTheme())
    } catch (e) { }
    finally { setPdfLoading(false); setProgress('') }
  }

  const isLoading = excelLoading || pdfLoading

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      {isLoading && progress && (
        <div className="flex items-center gap-3 p-4 bg-blue-900/30 border border-blue-800 rounded-xl text-blue-300 text-sm">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          {progress}
        </div>
      )}
      <button
        onClick={handleExcel}
        disabled={isLoading}
        className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-green-500/50 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
          {excelLoading ? <div className="animate-spin w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full" /> : <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[var(--color-text)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">Download Run Excel</div>
          <div className="text-sm text-[var(--color-text-muted)] mt-0.5">{`.xlsx with run summary, student overview, per-${t.lesson.lower} sheets`}</div>
        </div>
        <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors shrink-0" />
      </button>
      <button
        onClick={handlePdf}
        disabled={isLoading}
        className="w-full flex items-center gap-4 p-6 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl hover:border-red-500/30 hover:bg-[var(--color-surface-hover)] transition-all text-left group disabled:opacity-60"
      >
        <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
          {pdfLoading ? <div className="animate-spin w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full" /> : <FileText className="w-6 h-6 text-red-600 dark:text-red-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[var(--color-text)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">Download Run PDF</div>
          <div className="text-sm text-[var(--color-text-muted)] mt-0.5">Printable report with run summary, student overview, performance tables</div>
        </div>
        <Download className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors shrink-0" />
      </button>
    </motion.div>
  )
}
